import { useEffect, useReducer } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Box, Form, Header, Heading, Text } from 'grommet';

import {
  ActionButtons,
  ApplicationUrl,
  Category,
  CompanyName,
  ContactPreference,
  Disclaimer,
  Email,
  LanguagePreference,
  Name,
  Phone,
  PhoneWithCountryCode,
  Country,
  PassportId,
} from '.';

import {
  DEFAULT_COUNTRY_CODE,
  getCountryCallingCode,
  getParsedPhoneNumber,
  getParsedCountryCode,
  getParsedPhoneNumberWithoutCountryCode,
} from '@services/phoneNumberService';

import type { FormMarkupProps } from '@customTypes/types';
import type { FormMarkupState } from '@customTypes/stateTypes';

export const FormMarkup = (props: FormMarkupProps): JSX.Element => {
  const [state, updateState] = useReducer(
    (prev: FormMarkupState, newState: Partial<FormMarkupState>) => ({
      ...prev,
      ...newState,
    }),
    {
      phoneNumber: DEFAULT_COUNTRY_CODE,
      countryCode: DEFAULT_COUNTRY_CODE,
      phoneNumberWithoutCountryCode: ''
    }
  );

  useEffect(() => {
    document.title = props.formTitle;
    getGeoData();
    !props.translate && document.querySelector('html')?.setAttribute('translate', 'no');
  }, [props.formTitle, props.translate]);

  const isMobile: boolean = useMediaQuery({ query: '(max-width: 767px)' });

  const getGeoData = async () => {
    const countryCallingCode = await getCountryCallingCode();
    updateState({ phoneNumber: countryCallingCode, countryCode: countryCallingCode, phoneNumberWithoutCountryCode: '' });
  };

  const onPhoneNumberChange = (value: string): void => {
    updateState({ phoneNumber: getParsedPhoneNumber(value) });
  };

  const onCountryCodeChange = (value: string): void => {
    const parsedValue: string = getParsedPhoneNumberWithoutCountryCode(value, state.phoneNumberWithoutCountryCode);
    updateState({ countryCode: getParsedCountryCode(value), phoneNumberWithoutCountryCode: parsedValue });
  };

  const onPhoneNumberWithoutCountryCodeChange = (value: string): void => {
    const parsedValue: string = getParsedPhoneNumberWithoutCountryCode(state.countryCode, value);
    updateState({ phoneNumberWithoutCountryCode: parsedValue });
  };

  let clearFormFieldsState;

  const onReset = (): void => {
    getGeoData();
    props.onReset && props.onReset();
    clearFormFieldsState && clearFormFieldsState();
  };

  const assignResetFunction = (formFieldsClearStateFunc: Function) => {
    clearFormFieldsState = formFieldsClearStateFunc;
  }

  return (
    <Box gap="medium" width="large" margin={{ horizontal: 'auto' }} pad={{ horizontal: isMobile ? 'large' : 'xlarge' }}>
      <Header
        direction="column"
        align="start"
        gap="xxsmall"
        pad={{ horizontal: 'xxsmall' }}
      >
        <Heading level={1} margin="none">
          {props.formTitle}
        </Heading>
      </Header>

      <Box gap="medium" pad={{ horizontal: 'xxsmall' }}>
        {props.disclaimer &&
          <Disclaimer
            text={props.disclaimer}
          />
        }

        <Form
          validate="blur"
          messages={{
            required: 'This is a required field.',
          }}
          onSubmit={(event) => props.onSubmit(event)}
          onReset={onReset}
        >
          {props.category &&
            <Category
              categoryName={props.category}
            />
          }

          <Name />
          <CompanyName />

          {props.passportId &&
            <PassportId />
          }

          {props.applicationUrl &&
            <ApplicationUrl />
          }

          <Email />

          {!props.doubleFieldPhone &&
            <Phone
              phoneNumber={state.phoneNumber}
              onPhoneNumberChange={onPhoneNumberChange}
            />
          }

          {props.doubleFieldPhone &&
            <PhoneWithCountryCode
              countryCode={state.countryCode}
              phoneNumberWithoutCountryCode={state.phoneNumberWithoutCountryCode}
              onCountryCodeChange={onCountryCodeChange}
              onPhoneNumberWithoutCountryCodeChange={onPhoneNumberWithoutCountryCodeChange}
            />
          }

          {props.country &&
            <Country />
          }

          <ContactPreference
            contactPreference={props.contact}
            onReset={assignResetFunction}
          />

          {props.supportLanguages &&
            <LanguagePreference
              options={props.supportLanguages}
              supportHoursAcknowledgement={props.supportHoursAcknowledgement}
              onReset={assignResetFunction}
            />
          }

          {props.formFields && !props.hideDetails &&
            <Box margin={{ vertical: 'small' }}>
              <Text size="large">
                Support Issue Details
              </Text>
            </Box>
          }

          {props.formFields}

          <ActionButtons />
        </Form>
      </Box>
    </Box>
  );
};
